import {Alert, Card, Typography} from 'antd';
import {CardProps} from 'antd/es/card';
import {
  SubscriptionFieldsFragment,
  SubscriptionStatus,
  useChargesQuery,
  useStoreQuery,
  useSubscriptionQuery
} from 'api/generated';
import {
  AnchorLink,
  ButtonLink,
  PageHeader,
  PaymentMethodOverview,
  Stack,
  TableCard
} from 'components';
import {ContactDetailsBlock} from 'components/ContactDetailsBlock';
import {CustomerBlock} from 'components/CustomerBlock';
import {PaymentMethodBlock} from 'components/PaymentMethodBlock';
import {PropertyList} from 'components/PropertyList';
import {SubscriptionStatusTag} from 'components/SubscriptionStatusTag';
import {formatIntAmount, formatUnixDate} from 'lib/formaters';
import {
  canCancel,
  canPause,
  canResume,
  canSendSubscriptionStatus,
  canUpdate,
  getIntervalLabel
} from 'lib/subscriptionUtils';
import moment from 'moment';
import {stringify} from 'querystring';
import React from 'react';
import {Link, RouteComponentProps, useHistory} from 'react-router-dom';
import {queryFiltersSchema} from 'routes/Payments';
import {encodeQueryParams} from 'use-query-params';
import {TraceDetailsBlock} from '../../../components/TraceDetailsBlock';
import {NotFound} from '../../NotFound';
import {PaymentsTable} from '../../Payments/PaymentsTable';
import {CancelSubscription} from './CancelSubscription';
import {PauseSubscription} from './PauseSubscription';
import {ResumeSubscription} from './ResumeSubscription';
import {SendSubscriptionStatus} from './SendSubscriptionStatus';
import {SubscriptionTimeline} from './SubscriptionTimeline';

const {Text} = Typography;

const getFilteredValueLink = (type: string, value?: string | null, label?: string | null) => {
  if (!value) return null;
  const queryParams = {
    [type]: value,
    createdAt: [moment().subtract(3, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  };
  return (
    <Link
      to={{
        pathname: '/payments',
        // @ts-ignore
        search: stringify(encodeQueryParams(queryFiltersSchema, queryParams))
      }}>
      {label || value}
    </Link>
  );
};

export const formatIfFuture = (date: number | null | undefined) => {
  if (!date) return null;
  return moment.unix(date).isAfter(moment()) ? formatUnixDate(date, true) : null;
};

const SIZE = 10;

export const Subscription: React.FC<RouteComponentProps<{subscriptionId: string}>> = ({match}) => {
  const {subscriptionId} = match.params;
  const history = useHistory();

  const {data, loading} = useSubscriptionQuery({
    variables: {id: subscriptionId},
    context: {useApolloNetworkStatus: false}
  });
  const subscription = data?.subscription as SubscriptionFieldsFragment;
  const {data: storeData} = useStoreQuery({
    variables: {id: subscription?.storeId!},
    skip: !subscription?.storeId
  });
  const {data: paymentsData, loading: paymentsLoading} = useChargesQuery({
    skip: !subscription,
    variables: {
      filter: {subscriptionId: {match: subscriptionId}},
      size: SIZE
    }
  });

  const payments = paymentsData?.charges?.items || [];
  const totalPayments = paymentsData?.charges?.total || 0;

  if (!subscription && !loading) {
    return (
      <NotFound
        message={
          <>
            Sorry, subscription with id <strong>{subscriptionId}</strong> does not exist
          </>
        }
      />
    );
  }

  const currency = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: subscription?.currency || 'EUR'
  });

  const amount = currency.format((subscription?.amount ?? 0) / 100);

  const title = subscription && (
    <Stack>
      <span>
        {formatIntAmount(subscription?.amount)} every {getIntervalLabel(subscription)}
      </span>
      <SubscriptionStatusTag subscription={subscription} style={{marginLeft: 15}} />
    </Stack>
  );

  const goToPayments = () => {
    const start = moment().subtract(1, 'year').format('YYYY-MM-DD');
    const end = moment().format('YYYY-MM-DD');
    history.push(`/payments?subscriptionId=${subscriptionId}&createdAt=${start}_${end}`);
  };

  const cardProps: CardProps = {
    style: {marginBottom: 20},
    loading,
    size: 'small'
  };

  return (
    <>
      <PageHeader
        loading={loading}
        title={title}
        backPath={`/accounts/${subscription?.accountId}?tab=subscriptions`}>
        {canPause(subscription) && <PauseSubscription subscription={subscription} />}
        {canSendSubscriptionStatus(subscription) && (
          <SendSubscriptionStatus subscription={subscription} />
        )}
        {canResume(subscription) && <ResumeSubscription subscription={subscription} />}
        {canCancel(subscription) && <CancelSubscription subscription={subscription} />}
        {canUpdate(subscription) && (
          <ButtonLink
            to={`/accounts/${subscription.accountId}/subscriptions/${subscription.id}/edit`}>
            Update
          </ButtonLink>
        )}
      </PageHeader>
      <Card
        title="Subscription"
        extra={
          <Text type="secondary" copyable>
            {subscription?.id}
          </Text>
        }
        {...cardProps}>
        <PropertyList
          omitEmpty
          properties={[
            {
              label: 'Date',
              value: formatUnixDate(subscription?.createdAt)
            },
            {
              label: 'Customer email',
              value: getFilteredValueLink('customerEmail', subscription?.customer?.email)
            },
            {
              label: 'Store',
              value: storeData?.store
                ? getFilteredValueLink('storeId', storeData.store.id, storeData.store.name)
                : null
            },
            {
              label: 'Payment method',
              value: <PaymentMethodOverview item={subscription} listView={false} />
            }
          ]}
        />
        {subscription?.pauseAtPeriodEnd && (
          <Alert
            message={
              <>
                Subscription will be paused at the end of the current period (
                {formatUnixDate(subscription?.currentPeriodEnd)} and resumed on{' '}
                <b>{formatUnixDate(subscription?.nextPaymentAt)}</b>
              </>
            }
            type="info"
            style={{marginTop: 20}}
            showIcon
          />
        )}
        {subscription?.status === SubscriptionStatus.PAUSED && (
          <Alert
            message={
              <>
                Subscription will be resumed on <b>{formatUnixDate(subscription?.nextPaymentAt)}</b>
              </>
            }
            type="info"
            style={{marginTop: 20}}
            showIcon
          />
        )}
        {subscription?.cancelAtPeriodEnd && (
          <Alert
            message={
              <>
                Subscription will be canceled at the end of the current period (
                {formatUnixDate(subscription?.currentPeriodEnd)})
              </>
            }
            type="info"
            style={{marginTop: 20}}
            showIcon
          />
        )}
      </Card>
      <TableCard
        title="Last payments"
        {...cardProps}
        extra={
          totalPayments > SIZE && <AnchorLink onClick={goToPayments}>View all payments</AnchorLink>
        }>
        <PaymentsTable payments={payments} loading={paymentsLoading} pagination={false} />
      </TableCard>
      <SubscriptionTimeline />
      <Card title="Details" {...cardProps}>
        <PropertyList
          omitEmpty
          properties={[
            {label: 'Amount', value: amount},
            {label: 'Description', value: subscription?.description},
            {label: 'Interval', value: getIntervalLabel(subscription)},
            {label: 'Status', value: subscription?.status},
            {
              label: 'Next payment at',
              value: formatUnixDate(subscription?.nextPaymentAt)
            },
            {
              label: 'Trial ends',
              value: formatIfFuture(subscription?.trialPeriodEnd)
            }
          ]}
        />
      </Card>
      {subscription?.customer && (
        <Card title={'Customer'} {...cardProps}>
          <CustomerBlock customer={subscription?.customer} />
        </Card>
      )}
      {subscription?.paymentMethod && (
        <Card title={'Payment method'} {...cardProps}>
          <PaymentMethodBlock
            paymentMethod={subscription?.paymentMethod}
            countryCode={subscription?.paymentMethod.card?.country || null}
          />
        </Card>
      )}
      {subscription?.billingDetails && (
        <Card title={'Billing details'} {...cardProps}>
          <ContactDetailsBlock contactDetails={subscription?.billingDetails} />
        </Card>
      )}
      {subscription?.shippingDetails && (
        <Card title={'Shipping details'} {...cardProps}>
          <ContactDetailsBlock contactDetails={subscription?.shippingDetails} />
        </Card>
      )}
      {subscription?.sessionDetails && (
        <Card title="Session" {...cardProps}>
          <TraceDetailsBlock traceDetails={subscription.sessionDetails} />
        </Card>
      )}
    </>
  );
};
